'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/field/styles.css';
import 'BomdisoftComponents/inputs/field/select/styles.css';
import { BottomSheet } from 'BomdisoftComponents/drawers/bottom-sheet';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { CheckIcon } from 'BomdisoftComponents/icons/check';
import { KeyboardArrowDownRoundedIcon } from 'BomdisoftComponents/icons/index';
import { Checkbox } from 'BomdisoftComponents/inputs/checkbox/index';
import { Field, type FieldSize } from 'BomdisoftComponents/inputs/field/index';
import { commitValue, commitValues } from 'BomdisoftComponents/inputs/field/select/commit';
import { readOptions } from 'BomdisoftComponents/inputs/field/select/options';
import { useCoarsePointer } from 'BomdisoftComponents/inputs/field/select/pointer';
import type { SelectOption } from 'BomdisoftComponents/inputs/field/select/render';
import { Menu, MenuItem, MenuRoleScope } from 'BomdisoftComponents/menu/index';
import { type ComponentProps, type KeyboardEvent, type Ref, useId, useRef, useState } from 'react';

/** `size` is omitted from the native select props because its own `size` is a
 * numeric row count, which would intersect this component's scale prop down to
 * `never`. */
export type SelectProps = Omit<ComponentProps<'select'>, 'ref' | 'size'> & {
  error?: boolean;
  helperText?: string;
  label?: string;
  /** Lands on the trigger, which is the element a reader focuses now that the
   *  native select is hidden. */
  ref?: Ref<HTMLButtonElement>;
  /** The same three steps a `Button` takes, with the same default, so a form's
   * inputs and its action buttons are never different heights. */
  size?: FieldSize;
  /** Lands on the trigger, so a test drives the control a reader drives rather
   *  than the hidden element behind it. */
  testId?: string;
  /** `bare` drops the `Field` chrome for a select that is a SEGMENT of a larger
   *  control rather than a field of its own: the phone input's dialling code, a
   *  grid footer's page size. Without it those nest a bordered box inside a
   *  bordered box. */
  variant?: 'bare' | 'field';
};

/** How long a typed run stays one word. Past this, the next key starts a fresh
 *  search rather than extending a search the reader has forgotten making. */
const TYPEAHEAD_WINDOW_MS = 500;

const noop = () => {};

/**
 * A value list that opens our own menu rather than the operating system's.
 *
 * A real `<select>` stays in the markup, hidden and out of the tab order, as the
 * value source. That is what keeps `event.target.value` and
 * `event.target.selectedOptions` genuine for every existing consumer, and keeps
 * the control participating in a form, without imitating an event.
 */
export const Select = ({ children, className, error, helperText, id, label, ref, size, testId, variant = 'field', ...rest }: SelectProps) => {
  const reactId = useId();
  const triggerId = id ?? `${reactId}-select`;
  const nativeId = `${reactId}-native`;
  const listboxId = `${reactId}-listbox`;
  const helperId = helperText ? `${reactId}-helper` : undefined;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef<HTMLSelectElement>(null);
  const typedRef = useRef({ at: 0, buffer: '' });
  const [open, setOpen] = useState(false);
  const [nested, setNested] = useState(false);

  /* A dropdown inside a dropdown opens to the SIDE, the same way a submenu row
     does. Dropped below its trigger it lands on top of the panel it came from,
     covering the controls beside it. Read when opening rather than at render,
     because the trigger has to be in the DOM to know what encloses it. */
  const show = () => {
    setNested(Boolean(triggerRef.current?.closest('[popover]')));
    setOpen(true);
  };
  const [activeIndex, setActiveIndex] = useState(-1);
  const coarse = useCoarsePointer();

  const options = readOptions(children);
  const fallback = rest.defaultValue === undefined ? (options[0]?.value ?? '') : String(rest.defaultValue);
  const [internal, setInternal] = useState(rest.multiple ? '' : fallback);
  const [internalMany, setInternalMany] = useState<string[]>([]);

  const current = rest.value === undefined ? internal : Array.isArray(rest.value) ? '' : String(rest.value);
  const selected = rest.value === undefined ? internalMany : Array.isArray(rest.value) ? rest.value.map(String) : [String(rest.value)];
  const chosen = options.find(option => option.value === current);
  const triggerLabel = rest.multiple
    ? options
        .filter(option => selected.includes(option.value))
        .map(option => option.label)
        .join(', ')
    : (chosen?.triggerLabel ?? chosen?.label ?? '');

  const choose = (value: string) => {
    setInternal(value);
    const native = nativeRef.current;
    if (native) commitValue(native, value);
    setOpen(false);
  };

  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter(candidate => candidate !== value) : [...selected, value];
    setInternalMany(next);
    const native = nativeRef.current;
    if (native) commitValues(native, next);
  };

  const activate = (value: string) => (rest.multiple ? toggle(value) : choose(value));

  const moveTo = (index: number) => {
    if (options.length === 0) return;
    setActiveIndex((index + options.length) % options.length);
  };

  const jumpToTyped = (key: string) => {
    const now = Date.now();
    const typed = typedRef.current;
    typed.buffer = now - typed.at > TYPEAHEAD_WINDOW_MS ? key : typed.buffer + key;
    typed.at = now;
    const match = options.findIndex(option => option.label.toLowerCase().startsWith(typed.buffer.toLowerCase()));
    if (match >= 0) setActiveIndex(match);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      show();
      const at = options.findIndex(option => option.value === current);
      moveTo(at < 0 ? 0 : at);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveTo(activeIndex + 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveTo(activeIndex - 1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      moveTo(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      moveTo(options.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) activate(option.value);
      return;
    }
    if (event.key.length === 1) jumpToTyped(event.key);
  };

  /* Two row shapes, following what the grid already ships: the Columns panel
     toggles several things with real checkboxes in a plain row, and the Density
     menu chooses one with a mark and the tone. A checkbox row cannot be a button
     because its input is interactive content, which is why the Columns panel
     rows are divs. */
  const checkRows = options.map(option => (
    <div className='bomdisoft-select-check-row' key={option.value}>
      <Checkbox checked={selected.includes(option.value)} className='bomdisoft-select-check-label' disabled={option.disabled} label={option.label} onChange={() => toggle(option.value)} size='small' />
    </div>
  ));

  /* The active row is painted, not only announced: a keyboard reader who can see
     the screen needs the cursor to move somewhere visible, and focus stays on
     the trigger throughout. */
  const optionRow = (option: SelectOption, index: number) => (
    <MenuItem
      className={joinClasses('bomdisoft-select-row', open && index === activeIndex && 'bomdisoft-select-active')}
      disabled={option.disabled}
      id={`${listboxId}-${option.value}`}
      key={option.value}
      onClick={() => choose(option.value)}
      selected={option.value === current}
      testId={testId ? `${testId}-option-${option.value}` : undefined}
    >
      <span aria-hidden='true' className='bomdisoft-select-mark'>
        {option.value === current ? <CheckIcon size={16} /> : null}
      </span>
      {option.description ? (
        <span className='bomdisoft-select-two-line'>
          <span>{option.label}</span>
          <span className='bomdisoft-select-description'>{option.description}</span>
        </span>
      ) : (
        option.label
      )}
    </MenuItem>
  );

  const runs = options.reduce<{ entries: { index: number; option: SelectOption }[]; group?: string }[]>((all, option, index) => {
    const last = all.at(-1);
    if (last !== undefined && last.group === option.group) {
      last.entries.push({ index, option });
      return all;
    }
    return [...all, { entries: [{ index, option }], group: option.group }];
  }, []);

  const optionRows = runs.flatMap(run =>
    run.group === undefined
      ? run.entries.map(entry => optionRow(entry.option, entry.index))
      : [
          <fieldset aria-label={run.group} className='bomdisoft-select-group' key={`group-${run.entries[0]?.index ?? 0}`}>
            <span aria-hidden='true' className='bomdisoft-select-group-head'>
              {run.group}
            </span>
            {run.entries.map(entry => optionRow(entry.option, entry.index))}
          </fieldset>,
        ],
  );

  const rows = rest.multiple ? checkRows : optionRows;

  const activeOption = open ? options[activeIndex] : undefined;

  const body = (
    <>
      {/* Controlled from the same state the rows read, because React rewrites a
          select's DOM selection on every render and would otherwise undo the
          write that just told the consumer about it. */}
      <select {...rest} aria-hidden='true' className='bomdisoft-select-native' id={nativeId} onChange={rest.onChange ?? noop} ref={nativeRef} tabIndex={-1} value={rest.multiple ? selected : current}>
        {children}
      </select>
      {/* popovertarget makes the trigger the listbox's invoker. Without it the
          light-dismiss algorithm resolves the press to whatever popover encloses
          the field, and closes the list on pointerdown. A sheet is not a popover,
          so the coarse path must not claim an invoker it does not have. */}
      <button
        aria-activedescendant={activeOption ? `${listboxId}-${activeOption.value}` : undefined}
        aria-controls={listboxId}
        aria-describedby={helperId ?? rest['aria-describedby']}
        aria-expanded={open}
        aria-haspopup={rest.multiple ? 'dialog' : 'listbox'}
        aria-invalid={error ? true : rest['aria-invalid']}
        /* A visible `label` names the trigger through Field's `htmlFor`, but a
           select with no visible caption has only what the caller passed, and
           that goes to the hidden native element with the rest of the props. So
           a toolbar select and every `bare` one, which has no Field at all,
           rendered a combobox with NO accessible name. Forwarded here because
           the trigger is the element a reader reaches; the native select behind
           it is `aria-hidden` and cannot carry the name for it. */
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby']}
        className={joinClasses('bomdisoft-field-select', 'bomdisoft-select-trigger', variant === 'bare' && 'bomdisoft-select-bare-trigger')}
        data-testid={testId}
        disabled={rest.disabled}
        id={triggerId}
        onClick={show}
        onKeyDown={handleKeyDown}
        popoverTarget={coarse ? undefined : listboxId}
        popoverTargetAction={coarse ? undefined : 'show'}
        ref={node => {
          triggerRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        role='combobox'
        type='button'
      >
        {triggerLabel}
      </button>
      <span aria-hidden='true' className='bomdisoft-field-chevron'>
        <KeyboardArrowDownRoundedIcon size={18} />
      </span>
      {coarse ? (
        <BottomSheet label={label ?? ''} onClose={() => setOpen(false)} open={open} testId='select-sheet'>
          {/* The rows are MenuItems and take their role from context, which only
              Menu provides. Without this the sheet was a listbox full of
              menuitems, which is not a contract a screen reader can read. */}
          <MenuRoleScope role={rest.multiple ? 'dialog' : 'listbox'}>
            <div className='bomdisoft-select-sheet-list' id={listboxId} role={rest.multiple ? 'group' : 'listbox'}>
              {rows}
            </div>
          </MenuRoleScope>
        </BottomSheet>
      ) : (
        <Menu anchorRef={triggerRef} id={listboxId} matchAnchorWidth onClose={() => setOpen(false)} open={open} placement={nested ? 'side' : 'below'} role={rest.multiple ? 'dialog' : 'listbox'}>
          {rows}
        </Menu>
      )}
    </>
  );

  if (variant === 'bare') {
    return <span className={joinClasses('bomdisoft-select-bare', className)}>{body}</span>;
  }

  return (
    <Field className={className} disabled={rest.disabled} error={error} helperId={helperId} helperText={helperText} inputId={triggerId} label={label} required={rest.required} size={size}>
      {body}
    </Field>
  );
};
